import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { Emitter } from '@patsnap/tahiti-core';
import type {
    ParagraphIssueGroup,
    SpecChapterParagraphIssue,
    SpecChapter,
} from '../../types';
import type { IssuesManagementService } from '../issues-management-service';
import { ProofReadingOrchestrationService } from '../proof-reading-orchestration-service';
import { BatchIgnoreService } from '../batch-ignore-service';
import _ from 'lodash';

@injectable()
export class IssuesManagementServiceImpl implements IssuesManagementService {
    private _issues: ParagraphIssueGroup[] = [];
    private _hasIssuesData = false;
    private _isFetchingIssues = false;
    private _allIssuesMap = new Map<string, ParagraphIssueGroup[]>();
    private _paragraphIssuesIndex = new Map<string, ParagraphIssueGroup>();
    private _chapters: SpecChapter[] = [];
    private _generatingIssueGroup: ParagraphIssueGroup | undefined;
    private _fixingIssueGroup: ParagraphIssueGroup | undefined;
    private _fetchOperation: Promise<void> | null = null;

    private _onIssuesChangedEmitter = new Emitter<ParagraphIssueGroup[]>();
    private _onIssuesDataStatusChangedEmitter = new Emitter<{ isLoading: boolean }>();
    private _onFetchingStatusChangedEmitter = new Emitter<{ isFetching: boolean, done: boolean }>();
    private _onActiveIssueChangedEmitter = new Emitter<{ activeIssue: ParagraphIssueGroup | undefined }>();
    private _onGeneratingIssueChangedEmitter = new Emitter<{ generatingIssue: ParagraphIssueGroup | undefined }>();
    private _onFixingIssueChangedEmitter = new Emitter<{ fixingIssue: ParagraphIssueGroup | undefined }>();
    private _onTotalIssuesChangedEmitter = new Emitter<{ total: number }>();
    private _onErrorEmitter = new Emitter<{ error: Error; context: string; recoverable: boolean }>();

    readonly onIssuesChanged = this._onIssuesChangedEmitter.event;
    readonly onIssuesDataStatusChanged = this._onIssuesDataStatusChangedEmitter.event;
    readonly onFetchingStatusChanged = this._onFetchingStatusChangedEmitter.event;
    readonly onActiveIssueChanged = this._onActiveIssueChangedEmitter.event;
    readonly onGeneratingIssueChanged = this._onGeneratingIssueChangedEmitter.event;
    readonly onFixingIssueChanged = this._onFixingIssueChangedEmitter.event;
    readonly onTotalIssuesChanged = this._onTotalIssuesChangedEmitter.event;
    readonly onError = this._onErrorEmitter.event;

    constructor(
        @inject(ProofReadingOrchestrationService)
        private _proofReadingOrchestrationService: ProofReadingOrchestrationService,
        @inject(BatchIgnoreService)
        private _batchIgnoreService: BatchIgnoreService,
    ) {
        this._subscribeToOrchestrationEvents();
    }

    getIssueGroups(): ParagraphIssueGroup[] {
        return this._issues;
    }

    getFilteredIssueGroups(): ParagraphIssueGroup[] {
        return this._issues.filter(issue => !issue.isIgnored && !issue.isResolved);
    }

    hasIssuesData(): boolean {
        return this._hasIssuesData;
    }

    getTotalIssues(): number {
        return this.getFilteredIssueGroups()
            .reduce((acc, item) => acc + item.issues.filter(issue => !issue.isIgnored).length, 0);
    }

    private _activeParagraphIssueGroup: ParagraphIssueGroup | undefined;

    getActiveParagraphIssueGroup(): ParagraphIssueGroup | undefined {
        // 激活状态现在由事件系统管理，通过 onActiveIssueChanged 获取
        // 这里返回 undefined，具体激活状态由 useIssues 组合函数管理
        return this._activeParagraphIssueGroup;
    }

    getRemainingIssuesByParagraph(paragraphIndex: string): number {
        const issueGroup = this._paragraphIssuesIndex.get(paragraphIndex);
        if (!issueGroup || issueGroup.isResolved || issueGroup.isIgnored) {
            return 0;
        }
        return issueGroup.issues.filter(issue => !issue.isIgnored).length;
    }

    getIssueGroupByParagraphIndex(paragraphIndex: string): ParagraphIssueGroup | null {
        const issueGroup = this._paragraphIssuesIndex.get(paragraphIndex);
        if (!issueGroup || issueGroup.isResolved || issueGroup.isIgnored) {
            return null;
        }
        return issueGroup;
    }

    async init(): Promise<void> {
        const chapters = await this._proofReadingOrchestrationService.loadChapters();
        this._chapters = chapters;
        // 触发核稿流程
        await this._performFetchIssues({ regenerate: false });
    }

    fetchIssues(): void {
        if (this._chapters.length === 0) {
            return;
        }

        // 如果有正在进行的操作，直接返回
        if (this._fetchOperation) {
            return;
        }

        // 创建原子操作
        this._fetchOperation = this._performFetchIssues({ regenerate: true });
    }

    private async _performFetchIssues(options: { regenerate: boolean }): Promise<void> {
        try {
            // 1. 设置获取状态
            this._isFetchingIssues = true;
            this._onFetchingStatusChangedEmitter.fire({ isFetching: true, done: false });
            this._setHasDataStatus(false);

            // 2. 重置所有相关状态
            this._setActiveParagraphIssueGroup(undefined);
            this.setIssueGroupGenerating(undefined);
            this.setIssueGroupFixing(undefined);

            // 3. 清空当前数据并通知
            this._issues = [];
            this._allIssuesMap.clear();
            this._updateIssuesAndFireEvents();

            // 4. 开始获取新数据
            await this._proofReadingOrchestrationService.findIssues({
                chapters: this._chapters,
                regenerate: options.regenerate,
            });
        } catch (error) {
            // 触发错误事件，提供上下文和恢复性信息
            console.error('Failed to fetch issues:', error);
            this._onErrorEmitter.fire({
                error: error as Error,
                context: 'fetch_issues',
                recoverable: true,
            });
        } finally {
            // 无论成功还是失败，都要重置获取状态
            this._isFetchingIssues = false;
            this._onFetchingStatusChangedEmitter.fire({ isFetching: false, done: true });

            // 激活第一个issueGroup（可能为undefined）
            const firstIssueGroup = this._issues.find(group =>
                !group.isResolved &&
                !group.isIgnored &&
                group.issues.length > 0 &&
                group.issues.some(issue => !issue.isIgnored),
            );
            this._setActiveParagraphIssueGroup(firstIssueGroup);

            this._fetchOperation = null;
        }
    }

    setIssueGroupActive(issueGroup: ParagraphIssueGroup): void {
        // 如果正在获取问题，则阻止激活任何问题组
        if (this._isFetchingIssues) {
            return;
        }

        if (issueGroup.isResolved) {
            return;
        }

        // 如果有问题组正在生成，则阻止激活其他问题组
        if (this._generatingIssueGroup) {
            return;
        }

        this._activateIssueGroup(issueGroup);
    }

    setIssueGroupActiveById(paragraphIndex: string, whenActive?: () => void): void {
        // 如果正在获取问题，则阻止激活任何问题组
        if (this._isFetchingIssues) {
            return;
        }

        // 如果有问题组正在生成，则阻止激活其他问题组
        if (this._generatingIssueGroup) {
            return;
        }

        // 根据段落索引查找问题组
        const issueGroup = this._paragraphIssuesIndex.get(paragraphIndex);

        // 如果找不到问题组或问题组已解决，则直接返回
        if (!issueGroup || issueGroup.isResolved) {
            return;
        }

        this._activateIssueGroup(issueGroup);

        try {
            whenActive?.();
        } catch (error) {
            console.error('Failed to execute whenActive:', error);
        }
    }

    /**
     * 激活指定的问题组
     * @param issueGroup 要激活的问题组
     */
    private _activateIssueGroup(issueGroup: ParagraphIssueGroup): void {
        this.setIssueGroupFixing(undefined);
        // 设置活跃问题组并触发事件
        this._setActiveParagraphIssueGroup(issueGroup);
    }

    setIssueGroupGenerating(issueGroup: ParagraphIssueGroup | undefined): void {
        this._generatingIssueGroup = issueGroup;
        this._onGeneratingIssueChangedEmitter.fire({ generatingIssue: issueGroup });
    }

    setIssueGroupFixing(issueGroup: ParagraphIssueGroup | undefined): void {
        this._fixingIssueGroup = issueGroup;
        this._onFixingIssueChangedEmitter.fire({ fixingIssue: issueGroup });
    }

    /**
     * 私有方法：设置活跃的问题组并触发相应事件
     */
    private _setActiveParagraphIssueGroup(issueGroup: ParagraphIssueGroup | undefined): void {
        this._activeParagraphIssueGroup = issueGroup;
        this._onActiveIssueChangedEmitter.fire({ activeIssue: issueGroup });
    }

    async ignoreParagraphIssues(issueGroup: ParagraphIssueGroup): Promise<void> {
        await this._performIgnoreOperation(
            'ignore_paragraph_issues',
            async () => {
                // 获取所有未忽略的问题ID
                const issueIds = issueGroup.issues
                    .filter(issue => !issue.isIgnored)
                    .map(issue => issue.id);

                if (issueIds.length > 0) {
                    await this._batchIgnoreService.batchIgnoreIssues(issueIds);
                }

                // 更新本地状态
                issueGroup.issues.forEach(issue => {
                    issue.isIgnored = true;
                });
                issueGroup.isIgnored = true;
            },
        );
    }

    async ignoreIssue(issue: SpecChapterParagraphIssue, issueGroup: ParagraphIssueGroup): Promise<void> {
        await this._performIgnoreOperation(
            'ignore_issue',
            async () => {
                // 如果问题还未被忽略，则调用批量忽略服务
                if (!issue.isIgnored) {
                    await this._batchIgnoreService.batchIgnoreIssues([issue.id]);
                }

                // 更新本地状态
                issue.isIgnored = true;
                issueGroup.isIgnored = issueGroup.issues.every(issue => issue.isIgnored);
            },
        );
    }

    private async _performIgnoreOperation(context: string, operation: () => Promise<void>): Promise<void> {
        try {
            await operation();
            // 检查并切换活跃的问题组
            this._checkAndSwitchActiveIssueGroup();
            // 触发事件通知UI更新
            this._fireIssuesChangedEvents();
        } catch (error) {
            // 触发错误事件
            this._onErrorEmitter.fire({
                error: error as Error,
                context,
                recoverable: true,
            });
            // 重新抛出错误，让调用者处理
            throw error;
        }
    }

    resolveCurrentAndNext(issueGroup: ParagraphIssueGroup): ParagraphIssueGroup | undefined {
        // 标记当前问题为已解决
        issueGroup.isResolved = true;
        // 触发事件通知UI更新
        this._fireIssuesChangedEvents();
        // 如果当前问题组正在修复，则退出修复状态
        if (this._fixingIssueGroup?.paragraphIndex === issueGroup.paragraphIndex) {
            this.setIssueGroupFixing(undefined);
        }

        // 性能优化：使用一次遍历查找下一个问题
        const currentIndex = this._issues.findIndex(issue => issue.paragraphIndex === issueGroup.paragraphIndex);
        let nextIssue: ParagraphIssueGroup | undefined;

        for (let i = currentIndex + 1; i < this._issues.length; i++) {
            const issue = this._issues[i];
            if (!issue.isResolved && !issue.isIgnored) {
                nextIssue = issue;
                break;
            }
        }

        // 激活下一个问题（可能为undefined）
        this._setActiveParagraphIssueGroup(nextIssue);

        return nextIssue;
    }

    private _subscribeToOrchestrationEvents(): void {
        this._proofReadingOrchestrationService.onIssuesLoadingStatusChanged((event) => {
            this._setHasDataStatus(event.isLoadingIssues);
        });

        this._proofReadingOrchestrationService.onChapterIssuesFound((event) => {
            // 更新特定章节的问题
            this._allIssuesMap.set(event.chapterType, event.issueGroups);
            this._updateIssuesAndFireEvents();
        });
    }

    private _setHasDataStatus(hasData: boolean): void {
        this._hasIssuesData = hasData;
        this._onIssuesDataStatusChangedEmitter.fire({ isLoading: !hasData });
    }

    private _updateIssuesAndFireEvents(issueGroups?: ParagraphIssueGroup[]): void {
        if (issueGroups) {
            this._issues = issueGroups;
            this._updateParagraphIssuesIndex();
        } else {
            // 合并所有章节的问题
            const allIssues: ParagraphIssueGroup[] = [];
            for (const chapterIssues of this._allIssuesMap.values()) {
                for (const issue of chapterIssues) {
                    allIssues.push(issue);
                }
            }
            this._issues = allIssues;
            this._updateParagraphIssuesIndex();
        }

        // 发射事件
        this._fireIssuesChangedEvents();
    }

    private _updateParagraphIssuesIndex(): void {
        this._paragraphIssuesIndex.clear();
        for (const issue of this._issues) {
            this._paragraphIssuesIndex.set(issue.paragraphIndex, issue);
        }
    }

    private _fireIssuesChangedEvents(): void {
        const filteredIssues = this.getFilteredIssueGroups();
        this._onIssuesChangedEmitter.fire(filteredIssues);
        this._onTotalIssuesChangedEmitter.fire({ total: this.getTotalIssues() });
    }

    /**
     * 检查当前活跃的问题组是否仍然有效，如果无效则切换到下一个可用的问题组
     */
    private _checkAndSwitchActiveIssueGroup(): void {
        const currentActive = this._activeParagraphIssueGroup;

        // 如果当前没有活跃的问题组，或者当前活跃的问题组仍然有效，则无需切换
        if (!currentActive || this._isIssueGroupAvailable(currentActive)) {
            return;
        }

        // 查找下一个可用的问题组
        const nextAvailableIssueGroup = this._findNextAvailableIssueGroup();

        // 设置下一个可用的问题组（可能为undefined）
        this._setActiveParagraphIssueGroup(nextAvailableIssueGroup);
    }

    /**
     * 检查问题组是否可用（未解决且未忽略，且包含未忽略的问题）
     */
    private _isIssueGroupAvailable(issueGroup: ParagraphIssueGroup): boolean {
        return !issueGroup.isResolved &&
               !issueGroup.isIgnored &&
               issueGroup.issues.some(issue => !issue.isIgnored);
    }

    /**
     * 查找下一个可用的问题组
     */
    private _findNextAvailableIssueGroup(): ParagraphIssueGroup | undefined {
        const currentActive = this._activeParagraphIssueGroup;
        if (!currentActive) {
            // 如果没有当前激活的问题组，则返回第一个可用的问题组
            return this._issues.find(issueGroup => this._isIssueGroupAvailable(issueGroup));
        }

        // 从当前激活的问题组之后开始查找下一个可用的问题组
        const currentIndex = _.findIndex(this._issues, item => item.paragraphIndex === currentActive.paragraphIndex);
        if (currentIndex !== -1) {
            // 从下一个位置开始查找
            for (let i = currentIndex + 1; i < this._issues.length; i++) {
                const issueGroup = this._issues[i];
                if (this._isIssueGroupAvailable(issueGroup)) {
                    return issueGroup;
                }
            }
        }

        // 如果在当前位置之后没有找到可用的问题组，则从头开始查找
        return this._issues.find(issueGroup => this._isIssueGroupAvailable(issueGroup));
    }

    dispose(): void {
        // 在 dispose 时不清理任何状态或事件订阅
        // 这样可以确保在 dispose 后继续响应事件
    }
}
